/**
 * Contact Submissions Hook
 * Fetch and manage contact form submissions (admin only)
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface SubmissionListItem {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  messageType: string;
  message: string;
  status: string;
  userId: string | null;
  isRegisteredUser: boolean;
  userDisplayName: string | null;
  adminReply: string | null;
  repliedAt: string | null;
  repliedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UseContactSubmissionsParams {
  page: number;
  limit: number;
  search?: string;
  status?: string;
  messageType?: string;
  userType?: 'all' | 'registered' | 'public';
}

export interface UseContactSubmissionsResult {
  submissions: SubmissionListItem[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useContactSubmissions(
  params: UseContactSubmissionsParams,
): UseContactSubmissionsResult {
  const { authFetch } = useAuth();
  const [submissions, setSubmissions] = useState<SubmissionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        ...(params.search && { search: params.search }),
        ...(params.status && { status: params.status }),
        ...(params.messageType && { messageType: params.messageType }),
        ...(params.userType && { userType: params.userType }),
      });

      const response = await authFetch(apiUrl(`/api/admin/contact/submissions?${queryParams}`));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch submissions');
      }

      const data = await response.json();
      setSubmissions(data.submissions);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [
    authFetch,
    params.page,
    params.limit,
    params.search,
    params.status,
    params.messageType,
    params.userType,
  ]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  return {
    submissions,
    total,
    pages,
    loading,
    error,
    refetch: fetchSubmissions,
  };
}
